// Page editor — create/edit a cms_pages row: slug, per-locale content
// (title, two paragraphs, SEO meta), active + position.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Save, FileText, ArrowLeft, ArrowRight, Trash2, Images } from "lucide-react";
import { supabase, slugify } from "@vinoga/db";
import { Button } from "@/components/ui/button";
import { SlugInput } from "@/components/admin/SlugInput";
import { useToast } from "@/hooks/use-toast";
import { useT } from "@/lib/i18n";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { mediaSrc } from "@/lib/media";

type Locale = "lv" | "en" | "ru";
const LOCALES: Locale[] = ["lv", "en", "ru"];

type LocaleFields = {
  title: string;
  mainParagraph: string;
  lastParagraph: string;
  metaTitle: string;
  metaDescription: string;
};

const emptyLocale = (): LocaleFields => ({
  title: "",
  mainParagraph: "",
  lastParagraph: "",
  metaTitle: "",
  metaDescription: "",
});

const inputClass =
  "w-full rounded-lg border border-border bg-card px-3 py-2 font-body text-sm text-foreground outline-none focus:border-primary placeholder:text-muted-foreground";

export function PageEditor() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const { toast } = useToast();
  const tr = useT();

  const [tab, setTab] = useState<Locale>("lv");
  const [slug, setSlug] = useState("");
  const [active, setActive] = useState(true);
  const [position, setPosition] = useState(0);
  const [translations, setTranslations] = useState<Record<Locale, LocaleFields>>({
    lv: emptyLocale(),
    en: emptyLocale(),
    ru: emptyLocale(),
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  // Photo gallery (slider on the storefront service pages) — ordered URLs.
  const [images, setImages] = useState<string[]>([]);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      const { data } = await supabase
        .from("cms_pages")
        .select("*")
        .eq("id", id!)
        .single();
      if (!data) {
        toast({ title: tr("common.notFound", "Not found"), variant: "destructive" });
        setLoading(false);
        return;
      }
      setSlug(data.slug ?? "");
      setActive(!!data.active);
      setPosition(data.position ?? 0);
      setImages(Array.isArray(data.images) ? data.images : []);
      const stored = (data.translations ?? {}) as Record<Locale, Partial<LocaleFields>>;
      setTranslations({
        lv: { ...emptyLocale(), ...(stored.lv ?? {}) },
        en: { ...emptyLocale(), ...(stored.en ?? {}) },
        ru: { ...emptyLocale(), ...(stored.ru ?? {}) },
      });
      setLoading(false);
    })();
  }, [id, isNew, toast, tr]);

  const slugPreview = useMemo(
    () => slug.trim() || slugify(translations.lv.title),
    [slug, translations.lv.title],
  );

  const setField = useCallback(
    (locale: Locale, field: keyof LocaleFields, value: string) => {
      setTranslations((prev) => ({
        ...prev,
        [locale]: { ...prev[locale], [field]: value },
      }));
    },
    [],
  );

  const save = useCallback(async () => {
    const finalSlug = slug.trim() || slugify(translations.lv.title);
    if (!finalSlug) {
      toast({ title: tr("pages.slugRequired", "Slug is required"), variant: "destructive" });
      return;
    }
    setSaving(true);
    const payload = { slug: finalSlug, translations, active, position, images };
    const res = isNew
      ? await supabase.from("cms_pages").insert(payload).select("id").single()
      : await supabase.from("cms_pages").update(payload).eq("id", id!);
    setSaving(false);
    if (res.error) {
      toast({
        title: tr("common.saveFailed", "Save failed"),
        description: res.error.message,
        variant: "destructive",
      });
      return;
    }
    toast({ title: tr("common.saved", "Saved") });
    navigate("/pages");
  }, [slug, translations, active, position, images, isNew, id, navigate, toast, tr]);

  const moveImage = (i: number, dir: -1 | 1) => {
    setImages((prev) => {
      const j = i + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j]!, next[i]!];
      return next;
    });
  };

  if (loading) {
    return <div className="p-6 text-sm text-muted-foreground">{tr("common.loading", "Loading…")}</div>;
  }

  const t = translations[tab];

  return (
    <div className="mx-auto max-w-3xl p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="flex items-center gap-2 font-display text-xl font-bold text-foreground">
          <FileText size={20} className="text-primary" />
          {isNew ? tr("pages.new", "New page") : tr("pages.edit", "Edit page")}
        </h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate("/pages")} disabled={saving}>
            {tr("common.cancel", "Cancel")}
          </Button>
          <Button onClick={save} disabled={saving} className="gap-2">
            <Save size={16} />
            {saving ? tr("common.saving", "Saving…") : tr("common.save", "Save")}
          </Button>
        </div>
      </div>

      <div className="space-y-4 rounded-lg border border-border bg-card p-5">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-semibold text-muted-foreground">
              {tr("pages.colSlug", "Slug")}
            </label>
            <SlugInput
              value={slug}
              onChange={setSlug}
              table="cms_pages"
              excludeId={isNew ? undefined : id}
              sourceText={translations.lv.title}
              className={inputClass + " font-mono text-xs"}
              placeholder={slugify(translations.lv.title || "par-vinogu")}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              /{slugPreview || "…"}
            </p>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-muted-foreground">
              {tr("pages.position", "Position")}
            </label>
            <input
              type="number"
              className={inputClass}
              value={position}
              onChange={(e) => setPosition(parseInt(e.target.value) || 0)}
            />
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
          />
          {tr("pages.activeHint", "Visible on the storefront")}
        </label>
      </div>

      <div className="mt-5 rounded-lg border border-border bg-card p-5">
        <div className="mb-4 flex gap-1">
          {LOCALES.map((l) => (
            <button
              key={l}
              onClick={() => setTab(l)}
              className={
                "rounded px-3 py-1 text-xs font-semibold uppercase " +
                (tab === l ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground")
              }
            >
              {l}
            </button>
          ))}
        </div>

        <div className="space-y-3">
          <Field
            label={tr("pages.fieldTitle", "Title")}
            value={t.title}
            onChange={(v) => setField(tab, "title", v)}
          />
          <Field
            label={tr("pages.fieldMain", "Main paragraph")}
            value={t.mainParagraph}
            onChange={(v) => setField(tab, "mainParagraph", v)}
            textarea
          />
          <Field
            label={tr("pages.fieldLast", "Closing paragraph")}
            value={t.lastParagraph}
            onChange={(v) => setField(tab, "lastParagraph", v)}
            textarea
          />
          <Field
            label={tr("pages.fieldMetaTitle", "SEO title")}
            value={t.metaTitle}
            onChange={(v) => setField(tab, "metaTitle", v)}
          />
          <Field
            label={tr("pages.fieldMetaDesc", "SEO description")}
            value={t.metaDescription}
            onChange={(v) => setField(tab, "metaDescription", v)}
            textarea
          />
        </div>
      </div>

      {/* Photo gallery — rendered as the slider on the storefront service
          pages (3 side by side on desktop, single on mobile). Order here =
          slider order. */}
      <div className="mt-5 rounded-lg border border-border bg-card p-5">
        <h2 className="mb-1 flex items-center gap-2 font-display text-sm font-bold text-foreground">
          <Images size={16} className="text-muted-foreground" />
          {tr("pages.gallery", "Galerija")}
        </h2>
        <p className="mb-4 text-xs text-muted-foreground">
          {tr(
            "pages.galleryHint",
            "Attēli tiek rādīti slaiderī zem lapas virsraksta (3 blakus uz datora, pa vienam mobilajā). Secība šeit = secība slaiderī.",
          )}
        </p>
        {images.length > 0 && (
          <div className="mb-4 grid grid-cols-3 gap-3 md:grid-cols-5">
            {images.map((url, i) => (
              <div key={url} className="group relative overflow-hidden rounded border border-border">
                <img
                  src={mediaSrc(url) ?? url}
                  alt=""
                  className="aspect-square w-full object-cover"
                />
                <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-background/85 px-1 py-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                  <button
                    type="button"
                    onClick={() => moveImage(i, -1)}
                    disabled={i === 0}
                    className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30"
                    aria-label="Move left"
                  >
                    <ArrowLeft size={13} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setImages((prev) => prev.filter((_, j) => j !== i))}
                    className="p-1 text-destructive"
                    aria-label="Remove"
                  >
                    <Trash2 size={13} />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveImage(i, 1)}
                    disabled={i === images.length - 1}
                    className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30"
                    aria-label="Move right"
                  >
                    <ArrowRight size={13} />
                  </button>
                </div>
                <span className="absolute left-1 top-1 rounded bg-background/85 px-1 text-[10px] font-semibold text-muted-foreground">
                  {i + 1}
                </span>
              </div>
            ))}
          </div>
        )}
        <ImageUpload
          bucket="banners"
          preserveAspectRatio
          onChange={(url) => {
            if (url) setImages((prev) => [...prev, url]);
          }}
          placeholder={tr("pages.galleryUpload", "Klikšķini vai ievelc attēlu, lai pievienotu")}
        />
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  textarea,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  textarea?: boolean;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold text-muted-foreground">{label}</label>
      {textarea ? (
        <textarea
          className={inputClass + " min-h-[90px] resize-y"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input className={inputClass} value={value} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}
